import { SubmissionReviewViewModel } from '@program-suite/data-common/program';
import { Component, computed, inject, input, OnInit, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonComponent } from '@program-suite/ui';
import { ViewSubmissionStore } from '@program-suite/data-common';
import { AuthStore } from '@program-suite/data-auth';
import { Router } from '@angular/router';

@Component({
  selector: 'lib-submission-review',
  imports: [CommonModule, ButtonComponent],
  templateUrl: './ui-submission-review.component.html',
  styleUrl: './ui-submission-review.component.scss',
})
export class SubmissionReviewComponent {
  authStore = inject(AuthStore);
  router = inject(Router);
  model = input.required<SubmissionReviewViewModel>();
  viewSubmissionStore = inject(ViewSubmissionStore);
  guid = input<string>();
  status = input<string>();

  closeModal = output<boolean>();

  // Older submissions predate field owners and carry payroll per class instead
  hasFieldOwners = computed(() => this.model()?.numberOfFieldOwners != null);

  // Memoized so the array reference is stable across change-detection passes. A plain getter returning
  // Object.entries(...) produced a fresh array each pass, which (with reference tracking in the @for)
  // triggered NG0100 ExpressionChangedAfterItHasBeenChecked when the model first populated.
  endorsements = computed<[string, string][]>(() => Object.entries(this.model()?.optionalCoverages ?? {}));

  // LIO General Liability coverage attributes (Quote step). Memoized for the same NG0100 reason as `endorsements`.
  generalLiabilityCoverage = computed<[string, string][]>(() =>
    Object.entries(this.model()?.generalLiabilityCoverage ?? {}),
  );

  // LIO detailed "% of Work" breakdown (Info step). Rendered in insertion (section) order via Object.entries
  // rather than the keyvalue pipe, which would sort alphabetically. Memoized for the NG0100 reason above.
  percentageOfWorkEntries = computed<[string, number][]>(() =>
    Object.entries(this.model()?.percentageOfWork ?? {}) as [string, number][],
  );

  /** LIO is third-party rated and has none of the RCIP-only review sections. */
  get isLio(): boolean {
    return this.model()?.programCode?.trim().toUpperCase() === 'LIO';
  }

  downloadDocument(guid: string) {
    this.viewSubmissionStore.downloadDocument(guid);
  }

  /** Opens the LIO third-party portal quote in a new tab. */
  openLioPortal() {
    const url = this.model()?.lioIndicationUrl;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  manageSubmission() {
    // LIO is edited in the LIO wizard: the RCIP wizard reads the flat `classes` list and would show LIO's
    // classifications/payroll (held on locations[].liabilityClasses) as blank.
    this.router.navigate([`${this.isLio ? '/lio-quote' : '/quote'}/${this.guid()}`])
    this.viewSubmissionStore.setSubmissionReview(null);
    this.closeModal.emit(true);
  }
}
